import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    input,
    output,
    signal,
    untracked,
    viewChild,
} from "@angular/core";

import { S25IconComponent } from "../modules/s25-icon/s25.icon.component";
import { TypeManagerDecorator } from "../main/type.map.service";

@TypeManagerDecorator("s25-ng-load-more")
@Component({
    selector: "s25-ng-load-more",
    template: `
        @if (hasMore()) {
            <div class="load-more-wrapper">
                <div class="load-more-info">Showing {{ currentCount() }} of {{ totalCount() }}</div>

                <div class="load-more-buttons">
                    <button class="load-more-btn" [attr.aria-disabled]="anyLoading()" (click)="onLoadMore()">
                        @if (isLoading() && !isLoadingAll()) {
                            <s25-ng-icon [type]="'refresh'" [size]="16" class="spinning" [ariaLabel]="false" />
                            <span>Loading...</span>
                        } @else {
                            <span>Load More</span>
                            <s25-ng-icon [type]="'arrowDown'" [size]="16" [ariaLabel]="false" />
                        }
                    </button>

                    @if (showLoadAll()) {
                        <button class="load-all-btn" [attr.aria-disabled]="anyLoading()" (click)="onLoadAll()">
                            @if (isLoadingAll()) {
                                <s25-ng-icon [type]="'refresh'" [size]="16" class="spinning" [ariaLabel]="false" />
                                <span>Loading All...</span>
                            } @else {
                                <span>Load All ({{ remaining() }})</span>
                            }
                        </button>
                    }
                </div>
            </div>
        } @else if (currentCount() > 0) {
            <div class="load-more-wrapper">
                <div #allLoadedStatus class="load-more-info" tabindex="-1">All {{ totalCount() }} items loaded</div>
            </div>
        }
    `,
    styles: `
        .load-more-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            padding: 2rem 0;
        }

        .load-more-info {
            font-size: 14px;
            color: var(--color--text-muted);
        }

        .load-more-buttons {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
            justify-content: center;
        }

        .load-more-btn,
        .load-all-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 2rem;
            background: var(--color--surface-bg);
            border: 2px solid var(--color--border);
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            color: var(--color--link-text);
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .load-more-btn:hover:not([aria-disabled="true"]),
        .load-all-btn:hover:not([aria-disabled="true"]) {
            background: var(--color--surface-bg-hover);
            border-color: var(--color--link-text);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px color-mix(in srgb, var(--color--link-text) 15%, transparent);
        }

        .load-more-btn:active:not([aria-disabled="true"]),
        .load-all-btn:active:not([aria-disabled="true"]) {
            transform: translateY(0);
        }

        .load-more-btn[aria-disabled="true"],
        .load-all-btn[aria-disabled="true"] {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .load-all-btn {
            background: var(--color--surface-2-bg);
            color: var(--color--text);
            border-color: var(--color--border-bold);
        }

        .load-all-btn:hover:not([aria-disabled="true"]) {
            background: var(--color--surface-2-bg-hover);
            border-color: var(--color--border-bold-hover);
        }

        ::ng-deep .spinning svg {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            from {
                transform: rotate(0deg);
            }
            to {
                transform: rotate(360deg);
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent],
})
export class S25LoadMoreComponent {
    currentCount = input.required<number>();
    totalCount = input.required<number>();
    isLoading = input<boolean>(false);
    isLoadingAll = input<boolean>(false);
    enableLoadAll = input<boolean>(true);

    loadMore = output<void>();
    loadAll = output<void>();

    hasMore = computed(() => this.currentCount() < this.totalCount());
    remaining = computed(() => this.totalCount() - this.currentCount());
    showLoadAll = computed(() => this.enableLoadAll() && this.remaining() > 0);
    anyLoading = computed(() => this.isLoading() || this.isLoadingAll());

    private readonly allLoadedStatus = viewChild<ElementRef<HTMLElement>>("allLoadedStatus");
    private readonly focusStatusWhenAllLoaded = signal(false);

    constructor() {
        effect(() => {
            const status = this.allLoadedStatus()?.nativeElement;
            if (!status || !untracked(this.focusStatusWhenAllLoaded)) return;

            this.focusStatusWhenAllLoaded.set(false);
            const focusWasDropped = !document.activeElement || document.activeElement === document.body;
            if (focusWasDropped) status.focus();
        });
    }

    onLoadMore(): void {
        if (this.hasMore() && !this.anyLoading()) {
            this.focusStatusWhenAllLoaded.set(true);
            this.loadMore.emit();
        }
    }

    onLoadAll(): void {
        if (this.hasMore() && !this.anyLoading()) {
            this.focusStatusWhenAllLoaded.set(true);
            this.loadAll.emit();
        }
    }
}
